import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Save, FolderOpen, Trash2, X, AlertTriangle } from "lucide-react";
import EmployeeCombobox from "@/components/EmployeeCombobox";

const STEPS = ["PERIOD", "HOURS & EARNINGS", "PRE-FLIGHT CHECK", "PREVIEW", "SUBMIT"];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function addDaysISO(iso, d) { const dt = new Date(iso); dt.setDate(dt.getDate() + d); return dt.toISOString().slice(0, 10); }

export default function NewPayrollRun() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod] = useState({
    period_start: addDaysISO(todayISO(), -13),
    period_end: todayISO(),
    pay_date: addDaysISO(todayISO(), 3),
  });
  const [lines, setLines] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [isBackfill, setIsBackfill] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [autosaveAt, setAutosaveAt] = useState(null);
  const [conflictAdmin, setConflictAdmin] = useState(null); // { admin_email, updated_at }
  const [recoveryDraft, setRecoveryDraft] = useState(null); // draft doc the banner offers to restore
  const [allEmployees, setAllEmployees] = useState([]); // every employee incl. terminated (final checks)
  // Memoized so the picker does not re-filter the whole roster on every render.
  const addableEmployees = useMemo(
    () => allEmployees.filter((e) => !lines.some((l) => l.employee_id === e.id)),
    [allEmployees, lines],
  );  const [loadedDraft, setLoadedDraft] = useState(null); // { id, name } when editing a saved draft
  const [addPick, setAddPick] = useState("");
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(false);
  const [preflight, setPreflight] = useState(null); // { rows, excluded, included }
  const [syncing, setSyncing] = useState(false);
  const [funding, setFunding] = useState(null); // delivery + fee preview for sign-off
  const [verification, setVerification] = useState(null); // per-employee payability (Tier 3 gate)
  // Autosave every 30s while the wizard is open — silent, no toast, so the
  // admin doesn't get spammed. Uses a fixed autosave name per browser tab so
  // repeated ticks upsert onto the same row instead of accumulating.
  const [autosaveKey] = useState(() => `⟳ autosave · ${new Date().toLocaleString()}`);
  const [sessionKey] = useState(() => `${Math.random().toString(36).slice(2, 10)}-${Date.now()}`);

  // Multi-tab lock heartbeat + on-mount recovery scan.
  useEffect(() => {
    let cancelled = false;
    const beat = async () => {
      try {
        const { data } = await api.post("/payroll/wizard/lock", { session_key: sessionKey });
        if (cancelled) return;
        if (data.conflict && data.others.length) {
          setConflictAdmin(data.others[0]);
        } else {
          setConflictAdmin(null);
        }
      } catch (err) { console.warn("payroll wizard heartbeat failed", err); }
    };
    beat();
    const id = setInterval(beat, 15000);
    // Recovery banner scan — offer to restore the newest autosave that
    // isn't this tab's own key and isn't older than 24h.
    (async () => {
      try {
        const { data } = await api.get("/payroll/drafts");
        const recent = data
          .filter((d) => (d.name || "").startsWith("⟳ autosave") && d.name !== autosaveKey)
          .filter((d) => {
            const age = Date.now() - new Date(d.updated_at).getTime();
            return age >= 0 && age < 24 * 60 * 60 * 1000;
          })
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        if (recent.length && !cancelled) setRecoveryDraft(recent[0]);
      } catch (err) { console.warn("autosave recovery scan failed", err); }
    })();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      // Skip when there's nothing to save (fresh mount, no employees loaded yet)
      if (!lines.length) return;
      try {
        await api.post("/payroll/drafts", {
          // Editing a loaded draft autosaves onto that exact row; a fresh
          // wizard falls back to this tab's autosave slot.
          id: loadedDraft?.id || undefined,
          name: loadedDraft?.name || autosaveKey,
          period_start: period.period_start,
          period_end: period.period_end,
          pay_date: period.pay_date,
          step,
          lines: lines.map(({ employee, ...l }) => l),
        });
        setAutosaveAt(new Date());
      } catch (err) {
        // Silent failure — autosave should never nag; next tick will retry.
        console.warn("Autosave failed:", err?.message || err);
      }
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, period, step, loadedDraft]);

  useEffect(() => {
    // Load once on mount — active/lines are derived state seeded from server, no
    // reactive deps required.
    (async () => {
      const { data } = await api.get("/employees");
      const active = data.filter((e) => e.status === "active");
      setAllEmployees(data);
      setEmployees(active);
      // No hardcoded default hours — the batch is built from approved
      // timesheets when the admin advances past the Period step.
      setLines(active.map((e) => ({
        employee_id: e.id, employee: e,
        hours_worked: 0, overtime_hours: 0, bonus: 0, other_deductions: 0,
      })));
      // Deep-link: ?draft={id} resumes that exact draft (Manage Drafts tab).
      const resumeId = params.get("draft");
      if (resumeId) {
        try {
          const { data: d } = await api.get(`/payroll/drafts/${resumeId}`);
          const pool = data;
          const empById = Object.fromEntries(pool.map((e) => [e.id, e]));
          const rebuilt = (d.lines || []).filter((l) => empById[l.employee_id]).map((l) => ({
            employee_id: l.employee_id, employee: empById[l.employee_id],
            hours_worked: Number(l.hours_worked || 0), overtime_hours: Number(l.overtime_hours || 0),
            bonus: Number(l.bonus || 0), other_deductions: Number(l.other_deductions || 0),
          }));
          if (rebuilt.length) setLines(rebuilt);
          setPeriod({ period_start: d.period_start, period_end: d.period_end, pay_date: d.pay_date });
          setLoadedDraft({ id: d.id, name: d.name });
          setStep(Math.max(1, Number(d.step || 1)));
          toast.success(`Resumed draft: ${d.name}`, { description: "Nothing is disbursed until you authorize the run." });
        } catch (err) { toast.error(formatApiError(err)); }
        params.delete("draft"); setParams(params, { replace: true });
        return;
      }
      // Auto-open the drafts picker when the dashboard "Saved Drafts" CTA sent us here.
      if (params.get("drafts") === "1") {
        try {
          const { data: dd } = await api.get("/payroll/drafts");
          setDrafts(dd);
          setShowDrafts(true);
        } catch (err) { toast.error(formatApiError(err)); }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updLine = (i, k, v) => {
    const next = [...lines]; next[i] = { ...next[i], [k]: Number(v) }; setLines(next);
  };

  const refreshDrafts = async () => {
    try {
      const { data } = await api.get("/payroll/drafts");
      setDrafts(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveDraft = async () => {
    const name = (draftName || loadedDraft?.name || `Draft — ${period.period_start} → ${period.period_end}`).trim();
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/drafts", {
        id: loadedDraft?.id || undefined,
        name,
        period_start: period.period_start,
        period_end: period.period_end,
        pay_date: period.pay_date,
        step,
        lines: lines.map(({ employee, ...l }) => l),
      });
      setLoadedDraft({ id: data.id, name: data.name });
      toast.success(`Draft saved: ${name}`, { description: "Nothing has been disbursed — this run stays in Drafts until you authorize it." });
      setDraftName("");
      await refreshDrafts();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const loadDraft = (d) => {
    setPeriod({ period_start: d.period_start || period.period_start, period_end: d.period_end || period.period_end, pay_date: d.pay_date || period.pay_date });
    // Rebuild the batch strictly from the draft's saved membership so removed
    // employees stay removed and added ones (incl. terminated) come back.
    let mergedLines = lines;
    if (Array.isArray(d.lines) && d.lines.length) {
      const pool = allEmployees.length ? allEmployees : employees;
      const empById = Object.fromEntries(pool.map((e) => [e.id, e]));
      const rebuilt = d.lines
        .filter((l) => empById[l.employee_id])
        .map((l) => ({
          employee_id: l.employee_id,
          employee: empById[l.employee_id],
          hours_worked: Number(l.hours_worked || 0),
          overtime_hours: Number(l.overtime_hours || 0),
          bonus: Number(l.bonus || 0),
          other_deductions: Number(l.other_deductions || 0),
        }));
      const dropped = d.lines.length - rebuilt.length;
      if (dropped > 0) toast.warning(`${dropped} employee line(s) skipped — those records no longer exist.`);
      mergedLines = rebuilt;
      setLines(rebuilt);
    }
    if (!(d.name || "").startsWith("⟳ autosave")) setLoadedDraft({ id: d.id, name: d.name });
    const nextStep = Number(d.step || 0);
    setStep(nextStep);
    setShowDrafts(false);
    params.delete("drafts"); setParams(params, { replace: true });
    toast.success(`Loaded: ${d.name}`);
    // Wizard Preview Refresh — if the draft was past step 1 (Hours), auto-run
    // Preview so the admin sees the restored tax impact without extra clicks.
    if (nextStep >= 2) {
      setTimeout(async () => {
        try {
          const payload = { period_start: d.period_start || period.period_start, period_end: d.period_end || period.period_end, pay_date: d.pay_date || period.pay_date, lines: mergedLines.map(({ employee, ...l }) => l) };
          const { data: pv } = await api.post("/payroll/preview", payload);
          setPreview(pv);
        } catch (err) { toast.error(formatApiError(err)); }
      }, 200);
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/payroll/drafts/${id}`);
      if (loadedDraft?.id === id) setLoadedDraft(null);
      toast.success("Draft deleted");
      await refreshDrafts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Batch membership — draft-only edits, never touches payroll history.
  const removeLine = (i) => {
    const gone = lines[i];
    setLines(lines.filter((_, idx) => idx !== i));
    setPreview(null);
    toast.message(`${gone.employee.first_name} ${gone.employee.last_name} removed from this batch`, { description: "Draft-only change — save the draft to keep it." });
  };

  const addEmployeeToBatch = (empId) => {
    const e = allEmployees.find((x) => x.id === empId);
    if (!e || lines.some((l) => l.employee_id === empId)) return;
    const synced = preflight?.rows?.find((r) => r.employee_id === empId);
    setLines([...lines, {
      employee_id: e.id, employee: e,
      hours_worked: Number(synced?.hours || 0),
      overtime_hours: Number(synced?.ot_hours || 0),
      bonus: 0, other_deductions: 0,
    }]);
    setPreview(null);
    setAddPick("");
    toast.success(`${e.first_name} ${e.last_name} added to this batch`, { description: e.status !== "active" ? "Terminated employee — use for final paychecks only." : "Draft-only change until you authorize the run." });
  };

  const deleteEntireDraft = async () => {
    if (!loadedDraft) return;
    setBusy(true);
    try {
      await api.delete(`/payroll/drafts/${loadedDraft.id}`);
      toast.success(`Draft "${loadedDraft.name}" permanently deleted`, { description: "No payroll history was affected — nothing had been disbursed." });
      setLoadedDraft(null);
      setConfirmDeleteDraft(false);
      setPreview(null);
      setStep(0);
      setLines(employees.map((e) => ({
        employee_id: e.id, employee: e,
        hours_worked: 0, overtime_hours: 0, bonus: 0, other_deductions: 0,
      })));
      await refreshDrafts();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const syncFromTimesheets = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get(`/payroll/preflight?start=${period.period_start}&end=${period.period_end}`);
      setPreflight(data);
      const kept = data.rows.filter((r) => r.include);
      const byId = Object.fromEntries(allEmployees.map((e) => [e.id, e]));
      setLines(kept.map((r) => ({
        employee_id: r.employee_id,
        employee: byId[r.employee_id] || { first_name: r.employee_name, last_name: "", employment_type: r.employment_type },
        hours_worked: Number(r.hours || 0),
        overtime_hours: Number(r.ot_hours || 0),
        bonus: 0, other_deductions: 0,
      })));
      setPreview(null);
      setStep(1);
      if (data.excluded.length) {
        toast.warning(`${data.excluded.length} employee(s) excluded — no approved hours this period`, {
          description: data.excluded.slice(0, 6).join(", ") + (data.excluded.length > 6 ? "…" : ""),
        });
      } else {
        toast.success(`Synced approved timesheet hours for ${kept.length} employee(s)`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncing(false);
  };

  // Pre-flight rows for the CURRENT batch, joining profile rate + synced hours.
  const preflightRows = lines.map((l) => {
    const salaried = l.employee.employment_type === "salary";
    const rate = salaried ? Number(l.employee.annual_salary || 0) : Number(l.employee.hourly_rate || 0);
    const totalHours = Number(l.hours_worked || 0) + Number(l.overtime_hours || 0);
    const pv = preview?.lines?.find((p) => p.employee_id === l.employee_id);
    const issues = [];
    if (rate <= 0) issues.push("ZERO RATE");
    if (!salaried && totalHours <= 0) issues.push("ZERO HOURS");
    return { line: l, salaried, rate, totalHours, gross: pv ? Number(pv.gross || 0) : null, issues };
  });
  const blocked = preflightRows.some((r) => r.issues.length > 0);

  const fixRate = async (row) => {
    const label = row.salaried ? "annual salary" : "hourly rate";
    const raw = window.prompt(`Enter the ${label} for ${row.line.employee.first_name} ${row.line.employee.last_name}:`, "");
    const val = Number(raw);
    if (!raw || !Number.isFinite(val) || val <= 0) return;
    try {
      const field = row.salaried ? "annual_salary" : "hourly_rate";
      const { data } = await api.put(`/employees/${row.line.employee_id}`, { [field]: val });
      setLines((prev) => prev.map((l) => (l.employee_id === row.line.employee_id ? { ...l, employee: data } : l)));
      setAllEmployees((prev) => prev.map((e) => (e.id === data.id ? data : e)));
      setPreview(null);
      toast.success(`Updated ${label} to ${fmtMoney(val)}`, { description: "Re-run the pre-flight check to refresh gross pay." });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const payload = { ...period, lines: lines.map(({ employee, ...l }) => l) };
      const { data } = await api.post("/payroll/preview", payload);
      setPreview(data);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  // Tier 3 gate — the wizard mirrors the server-side block so the button is
  // disabled before anyone clicks it.
  const blockedIds = new Set((verification?.blocked || []).map((b) => b.employee_id));
  const blockedLines = (preview?.lines || [])
    .filter((l) => blockedIds.has(l.employee_id))
    .map((l) => {
      const row = (verification?.blocked || []).find((b) => b.employee_id === l.employee_id) || {};
      return { employee_id: l.employee_id, name: row.name || l.name || "Employee", last4: row.last4 || "" };
    });

  const submitRun = async () => {
    if (blockedLines.length) {
      toast.error("Some accounts are pending verification", {
        description: blockedLines.map((b) => b.name).join(", ") + " — verify and authorize the rail in the employee's Payment Rails panel, or remove them from this run.",
      });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...period, is_backfill: isBackfill, lines: lines.map(({ employee, ...l }) => l) };
      const { data } = await api.post("/payroll/runs", payload);
      toast.success(isBackfill ? "Backfill run created — stubs are hidden until you release them" : "Payroll run created — stubs are hidden until you release them");
      nav(`/app/payroll`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-2">§ NEW PAYROLL RUN · ENTERPRISE WIZARD</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Run Payroll</h1>
          {loadedDraft && (
            <div className="mt-2 inline-flex items-center gap-2 border-2 border-ink bg-secondary px-3 py-1 font-mono text-[10px] uppercase tracking-widest" data-testid="editing-draft-badge">
              Editing draft · {loadedDraft.name}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loadedDraft && (
            <button data-testid="delete-entire-draft-btn" onClick={() => setConfirmDeleteDraft(true)} className="border-2 border-bad text-bad px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-bad hover:text-white">
              <Trash2 className="w-4 h-4" /> Delete Entire Draft
            </button>
          )}
          {autosaveAt && (
            <span className="font-mono text-[10px] text-good" data-testid="autosave-pill" title={`Autosaved as "${autosaveKey}"`}>
              ⟳ Autosaved {autosaveAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button data-testid="open-drafts-btn" onClick={async () => { await refreshDrafts(); setShowDrafts(true); }} className="border-2 border-ink px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white">
            <FolderOpen className="w-4 h-4" /> Drafts
          </button>
          <button data-testid="save-draft-btn" onClick={saveDraft} disabled={busy} className="border-2 border-ink bg-secondary px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white disabled:opacity-50">
            <Save className="w-4 h-4" /> Save as Draft
          </button>
        </div>
      </div>

      {/* Steps */}
      {conflictAdmin && (
        <div className="mb-6 border-4 border-bad bg-bad/10 p-4 flex items-center justify-between gap-4" data-testid="conflict-banner">
          <div>
            <div className="font-heading font-black text-bad text-lg">⚠ Another admin is editing this company's payroll</div>
            <div className="font-mono text-xs text-muted-foreground mt-1">
              <b>{conflictAdmin.admin_email || "Another admin"}</b> has this wizard open (last active {new Date(conflictAdmin.updated_at).toLocaleTimeString()}). Autosave from both tabs may clobber each other's hours — coordinate before submitting.
            </div>
          </div>
          <button onClick={() => setConflictAdmin(null)} className="border-2 border-bad text-bad px-3 py-2 font-mono uppercase text-[10px] tracking-widest hover:bg-bad hover:text-white" data-testid="conflict-dismiss">
            Dismiss
          </button>
        </div>
      )}
      {recoveryDraft && (
        <div className="mb-6 border-4 border-warn bg-warn/10 p-4 flex items-center justify-between gap-4" data-testid="recovery-banner">
          <div>
            <div className="font-heading font-black text-lg">Restore latest autosave from {new Date(recoveryDraft.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}?</div>
            <div className="font-mono text-xs text-muted-foreground mt-1">
              We found an in-progress payroll from a previous session ({recoveryDraft.lines?.length || 0} employee lines · step {recoveryDraft.step}). Restore it to pick up exactly where you left off.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadDraft(recoveryDraft); setRecoveryDraft(null); }} className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest" data-testid="recovery-restore">
              Restore →
            </button>
            <button onClick={() => setRecoveryDraft(null)} className="border-2 border-ink px-3 py-2 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-white" data-testid="recovery-dismiss">
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-10 font-mono text-xs uppercase tracking-widest">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 border-2 border-ink flex items-center justify-center ${i <= step ? "bg-ink text-white" : "bg-white"}`}>{i + 1}</div>
            <div className={i === step ? "font-black" : "text-muted-foreground"}>{s}</div>
            {i < STEPS.length - 1 && <div className="w-8 h-0.5 bg-ink mx-1 hidden sm:block" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="hard-card p-8 max-w-2xl">
          <div className="overline mb-4">Set Pay Period</div>
          <div className="grid grid-cols-3 gap-4">
            <label>
              <div className="overline mb-2">Period Start</div>
              <input type="date" data-testid="period-start" value={period.period_start} onChange={(e) => setPeriod({ ...period, period_start: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label>
              <div className="overline mb-2">Period End</div>
              <input type="date" data-testid="period-end" value={period.period_end} onChange={(e) => setPeriod({ ...period, period_end: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label>
              <div className="overline mb-2">Pay Date</div>
              <input type="date" data-testid="pay-date" value={period.pay_date} onChange={(e) => setPeriod({ ...period, pay_date: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
          </div>
          <label className="mt-5 flex items-start gap-3 cursor-pointer p-3 border-2 border-ink bg-secondary/40" data-testid="backfill-row">
            <input
              data-testid="backfill-checkbox"
              type="checkbox"
              checked={isBackfill}
              onChange={(e) => setIsBackfill(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-ink"
            />
            <div>
              <div className="font-mono text-xs font-black">HISTORICAL / BACKFILL RUN</div>
              <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">Tick this for past pay periods you're bringing into Checkmate after the fact. Stubs are still generated but the employee is NOT emailed on release — perfect for data migration or catch-up entry.</div>
            </div>
          </label>
          <button data-testid="step-next-1" disabled={employees.length === 0 || syncing} onClick={syncFromTimesheets} className="mt-6 hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
            {syncing ? "Syncing timesheets…" : "Sync Approved Hours"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-3 font-mono text-[10px] text-muted-foreground leading-relaxed max-w-lg">
            Hours are pulled live from approved timesheets for this period — no default hours are ever assumed. Employees with zero approved hours are dropped from the batch automatically.
          </div>
          {employees.length === 0 && <div className="mt-4 text-bad font-mono text-xs">Add active employees before running payroll.</div>}
        </div>
      )}

      {step === 1 && (
        <div>
          <div className="hard-card overflow-x-auto">
            <table className="w-full font-mono text-sm" data-testid="hours-table">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Employee</th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Type</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Hours</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">OT Hours</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Bonus</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Other Ded.</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">—</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.employee_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"}>
                    <td className="px-4 py-3 border-t border-ink/20 font-semibold">
                      {l.employee.first_name} {l.employee.last_name}
                      {l.employee.status !== "active" && <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-bad border border-bad px-1">{l.employee.status}</span>}
                    </td>
                    <td className="px-4 py-3 border-t border-ink/20 uppercase">{l.employee.employment_type}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right"><input data-testid={`hours-${l.employee_id}`} type="number" step="0.25" value={l.hours_worked} onChange={(e) => updLine(i, "hours_worked", e.target.value)} className="w-24 text-right border-2 border-ink px-2 py-1" disabled={l.employee.employment_type === "salary"} /></td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right"><input type="number" step="0.25" value={l.overtime_hours} onChange={(e) => updLine(i, "overtime_hours", e.target.value)} className="w-20 text-right border-2 border-ink px-2 py-1" disabled={l.employee.employment_type === "salary"} /></td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right"><input type="number" step="0.01" value={l.bonus} onChange={(e) => updLine(i, "bonus", e.target.value)} className="w-24 text-right border-2 border-ink px-2 py-1" /></td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right"><input type="number" step="0.01" value={l.other_deductions} onChange={(e) => updLine(i, "other_deductions", e.target.value)} className="w-24 text-right border-2 border-ink px-2 py-1" /></td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right">
                      <button data-testid={`remove-line-${l.employee_id}`} onClick={() => removeLine(i)} title="Remove from this batch" className="p-1.5 border-2 border-bad text-bad hover:bg-bad hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground uppercase text-xs tracking-widest">No employees in this batch — add at least one below.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 hard-card p-4 flex flex-wrap items-end gap-3" data-testid="add-employee-panel">
            <label className="flex-1 min-w-[240px]">
              <div className="overline mb-2">Add Employee To Batch</div>
              <EmployeeCombobox
                employees={addableEmployees}
                value={addPick}
                onChange={setAddPick}
                scope="payroll-batch"
                testid="add-employee-select"
                placeholder="Search name or employee ID…"
                meta={(e) => `${e.employment_type}${e.status !== "active" ? ` · ${e.status.toUpperCase()}` : ""} · ID ${e.id}`}
              />
            </label>
            <button data-testid="add-employee-btn" disabled={!addPick} onClick={() => addEmployeeToBatch(addPick)} className="hard-btn px-5 py-2.5 font-mono uppercase text-xs tracking-widest disabled:opacity-40">
              + Add Employee
            </button>
            <div className="font-mono text-[10px] text-muted-foreground max-w-sm leading-relaxed">
              Terminated employees can be added for final paychecks. Batch changes stay in the draft — payroll history is untouched until you authorize the run.
            </div>
          </div>
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(0)} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button data-testid="step-preview" disabled={busy || lines.length === 0} onClick={runPreview} className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
              Run Pre-Flight Check <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="preflight-screen">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline mb-1">§ RECONCILIATION AUDIT</div>
              <h2 className="font-heading font-black text-3xl">Pre-Flight Check</h2>
              <div className="font-mono text-[11px] text-muted-foreground mt-1">
                Profile rates reconciled against approved timesheet hours for {period.period_start} → {period.period_end}.
              </div>
            </div>
            {!preview && (
              <button onClick={runPreview} disabled={busy || !lines.length} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40" data-testid="preflight-refresh">
                {busy ? "Calculating…" : "↻ Recalculate Gross Pay"}
              </button>
            )}
          </div>

          {blocked && (
            <div className="mb-4 border-4 border-bad bg-bad/10 p-4" data-testid="preflight-blocker">
              <div className="font-heading font-black text-bad text-lg">⚠ Discrepancies found — payroll is blocked</div>
              <div className="font-mono text-xs text-muted-foreground mt-1">
                Every highlighted row has a $0 profile rate or 0 hours for this period. Fix the rate or remove the employee from the batch to continue.
              </div>
            </div>
          )}
          {!!preflight?.excluded?.length && (
            <div className="mb-4 border-2 border-ink bg-secondary/50 p-3 font-mono text-[11px]" data-testid="preflight-excluded">
              <b>{preflight.excluded.length} employee(s) auto-excluded</b> (zero approved hours this period): {preflight.excluded.slice(0, 8).join(", ")}{preflight.excluded.length > 8 ? ` … +${preflight.excluded.length - 8} more` : ""}
            </div>
          )}

          <div className="hard-card overflow-x-auto">
            <table className="w-full font-mono text-sm" data-testid="preflight-table">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Employee Name</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Profile Rate</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Timesheet Hours</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Gross Pay</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody>
                {preflightRows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground uppercase text-xs tracking-widest">No employees in this batch.</td></tr>
                )}
                {preflightRows.map((r, i) => (
                  <tr
                    key={r.line.employee_id}
                    data-testid={`preflight-row-${r.line.employee_id}`}
                    className={r.issues.length ? "bg-bad/15" : (i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]")}
                  >
                    <td className={`px-4 py-3 border-t border-ink/20 font-semibold ${r.issues.length ? "text-bad" : ""}`}>
                      {r.line.employee.first_name} {r.line.employee.last_name}
                      {r.issues.map((iss) => (
                        <span key={iss} className="ml-2 border-2 border-bad text-bad px-1.5 py-0.5 text-[9px] uppercase tracking-widest" data-testid={`preflight-issue-${r.line.employee_id}`}>{iss}</span>
                      ))}
                    </td>
                    <td className={`px-4 py-3 border-t border-ink/20 text-right ${r.rate <= 0 ? "text-bad font-black" : ""}`}>
                      {fmtMoney(r.rate)}{r.salaried ? " /yr" : " /hr"}
                    </td>
                    <td className={`px-4 py-3 border-t border-ink/20 text-right ${!r.salaried && r.totalHours <= 0 ? "text-bad font-black" : ""}`}>
                      {r.salaried ? "— salaried" : `${r.totalHours.toFixed(2)} h`}
                    </td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right font-black">
                      {r.gross === null ? "—" : fmtMoney(r.gross)}
                    </td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {r.rate <= 0 && (
                          <button onClick={() => fixRate(r)} data-testid={`preflight-fix-rate-${r.line.employee_id}`} className="border-2 border-ink bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white">
                            Edit Rate
                          </button>
                        )}
                        <button onClick={() => removeLine(lines.findIndex((l) => l.employee_id === r.line.employee_id))} data-testid={`preflight-remove-${r.line.employee_id}`} className="p-1.5 border-2 border-bad text-bad hover:bg-bad hover:text-white">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button
              data-testid="preflight-continue"
              disabled={blocked || busy || !lines.length || !preview}
              title={blocked ? "Resolve every red row before processing payroll" : (!preview ? "Calculate gross pay first" : "Continue to the tax preview")}
              onClick={() => setStep(3)}
              className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
            >
              Process Payroll <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && preview && (
        <div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Stat label="Total Gross" val={preview.totals.gross} />
            <Stat label="Total Taxes" val={preview.totals.total_deductions} color="text-bad" />
            <Stat label="Total Net" val={preview.totals.net_pay} color="text-good" />
            <Stat label="Employees" val={preview.lines.length} money={false} />
          </div>
          <div className="hard-card overflow-x-auto">
            <table className="w-full font-mono text-sm">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-3 py-2 uppercase text-xs">Employee</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">Gross</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">Fed</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">SS</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">Medicare</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">State</th>
                  <th className="text-right px-3 py-2 uppercase text-xs">Net</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr key={l.employee_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"}>
                    <td className="px-3 py-2 border-t border-ink/20">{l.employee_name}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right">{fmtMoney(l.gross)}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right text-bad">{fmtMoney(l.federal_tax)}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right text-bad">{fmtMoney(l.social_security)}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right text-bad">{fmtMoney(l.medicare)}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right text-bad">{fmtMoney(l.state_tax)}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right font-black">{fmtMoney(l.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(2)} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button data-testid="step-submit-page" onClick={async () => {
              try {
                const { data } = await api.post("/funding/fee-quote", {
                  lines: (preview?.lines || []).map((l) => ({ employee_id: l.employee_id, net_pay: l.net_pay })),
                });
                setFunding(data);
              } catch (err) { console.warn("fee quote unavailable (panel is informational)", err); }
              try {
                const { data } = await api.get("/bank/verification-report");
                setVerification(data);
              } catch (err) { console.warn("verification report unavailable (gate is enforced server-side)", err); }
              setStep(4);
            }} className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
              Continue to Submit <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 4 && preview && (
        <div className="hard-card p-8 max-w-2xl">
          <div className="overline mb-2">§ CONFIRMATION</div>
          <h2 className="font-heading font-black text-3xl mb-4">Authorize Payroll</h2>
          <p className="text-sm text-muted-foreground mb-6">Once submitted, pay stubs will be generated and printable checks will be available for download. This action is idempotent per-click.</p>
          <div className="font-mono text-sm space-y-2 mb-6 border-2 border-ink p-4">
            <div className="flex justify-between"><span>Pay date</span><span>{period.pay_date}</span></div>
            <div className="flex justify-between"><span>Employees</span><span>{preview.lines.length}</span></div>
            <div className="flex justify-between font-black text-lg"><span>Total Payroll Cost (gross)</span><span>{fmtMoney(preview.totals.net_pay + preview.totals.total_deductions)}</span></div>
          </div>

          {funding && (
            <div className="font-mono text-xs border-2 border-ink p-4 mb-6" data-testid="funding-fee-panel">
              <div className="overline mb-2">§ DELIVERY &amp; FEES</div>
              {funding.funding_mode === "instant_card" ? (
                <>
                  <div className="flex justify-between"><span>Rail</span><b>Business debit card · Instant Delivery</b></div>
                  <div className="flex justify-between"><span>Instant payouts</span><span>{funding.instant_count}</span></div>
                  <div className="flex justify-between"><span>Standard payouts (bank destinations)</span><span>{funding.standard_count}</span></div>
                  <div className="flex justify-between mt-2 pt-2 border-t border-ink/30"><span>Stripe convenience fee — {funding.fee_rate}</span><b className="text-bad">{fmtMoney(funding.fee_total)}</b></div>
                  <div className="flex justify-between font-black"><span>Total cost with fees</span><span>{fmtMoney(funding.grand_total)}</span></div>                  <div className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                    Your business absorbs the convenience fee — employee net pay is unchanged.
                    Switch to the linked bank account in Company Settings for $0 fees on a 48h ACH schedule.
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span>Rail</span><b>{funding.funding_mode === "ach_bank" ? "Linked bank account · Standard ACH" : "Not configured"}</b></div>
                  <div className="flex justify-between"><span>Convenience fee</span><b className="text-good">{fmtMoney(0)}</b></div>
                  <div className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                    {funding.funding_mode === "ach_bank"
                      ? "Standard transfers clear in about 48 hours. Arm the business debit card in Company Settings for instant delivery."
                      : "No funding source selected yet — set one in Company Settings before processing payouts."}
                  </div>
                </>
              )}
              {funding.coverage && (
                <div
                  data-testid="funding-coverage-alert"
                  className={`mt-3 p-3 border-2 flex items-start gap-2 ${
                    funding.coverage.status === "short" ? "border-bad bg-bad/10 text-bad"
                    : funding.coverage.status === "unknown" ? "border-warn bg-warn/10" : "border-good bg-good/10"}`}
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="leading-relaxed">
                    <b className="uppercase tracking-widest text-[10px] block mb-0.5">
                      {funding.coverage.status === "short" ? "Insufficient funding"
                        : funding.coverage.status === "unknown" ? "Balance unverified" : "Funding covers this run"}
                    </b>
                    {funding.coverage.message}
                  </div>
                </div>
              )}
            </div>
          )}
          {blockedLines.length > 0 && (
            <div className="border-2 border-bad bg-bad/10 p-4 mb-6 font-mono text-xs" data-testid="pending-verification-block">
              <div className="font-black uppercase tracking-widest text-[10px] mb-1 text-bad">Pending Verification — payroll blocked</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {blockedLines.map((b) => (
                  <li key={b.employee_id} data-testid={`pending-verification-${b.employee_id}`}>
                    {b.name} — bank ••••{b.last4} awaiting Stripe micro-deposit confirmation
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-[10px] text-muted-foreground">
                Confirm their micro-deposits in Employees → the employee's Payment Rails panel, then authorize
                the rail. The employee portal is read-only, so only an administrator can complete this.
              </div>
            </div>
          )}
          <label className="flex items-center gap-3 mb-6">
            <input type="checkbox" data-testid="authorize-check" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="w-5 h-5 border-2 border-ink" />            <span className="font-mono text-sm">I authorize this payroll disbursement.</span>
          </label>
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest"><ArrowLeft className="w-4 h-4 inline mr-1" /> Back</button>
            <button data-testid="submit-payroll" disabled={!confirm || busy || blockedLines.length > 0} onClick={submitRun} className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
              <Check className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Payroll"}
            </button>
          </div>
        </div>
      )}
      {confirmDeleteDraft && loadedDraft && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4" data-testid="delete-draft-modal">
          <div className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg p-8">
            <div className="overline mb-2">§ PERMANENT ACTION</div>
            <h2 className="font-heading font-black text-3xl mb-3">Delete entire draft?</h2>
            <p className="font-mono text-sm text-muted-foreground mb-6 leading-relaxed">
              <b>{loadedDraft.name}</b> and all {lines.length} employee line(s) in it will be permanently removed. No pay stubs were generated and no funds were moved, so your payroll history is unaffected. This cannot be undone.
            </p>
            <div className="flex justify-between">
              <button onClick={() => setConfirmDeleteDraft(false)} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest" data-testid="delete-draft-cancel">Cancel</button>
              <button onClick={deleteEntireDraft} disabled={busy} className="px-6 py-3 border-2 border-bad bg-bad text-white font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50" data-testid="delete-draft-confirm">
                <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : "Delete Draft"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDrafts && <DraftsModal drafts={drafts} onClose={() => setShowDrafts(false)} onLoad={loadDraft} onDelete={deleteDraft} />}
    </div>
  );
}

function Stat({ label, val, money = true, color = "" }) {
  return (
    <div className="hard-card p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black text-2xl ${color}`}>{money ? fmtMoney(val) : val}</div>
    </div>
  );
}

// Drafts modal is inlined here so the entire "Enterprise Wizard" batch UX
// lives in one file — kept small (< 60 LOC) so it doesn't warrant a split.
export function DraftsModal({ drafts, onClose, onLoad, onDelete }) {
  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4" data-testid="drafts-modal">
      <div className="bg-white border-2 border-ink w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-hard-lg">
        <div className="flex items-center justify-between border-b-2 border-ink px-6 py-4 sticky top-0 bg-white">
          <div>
            <div className="overline">§ SAVED DRAFTS</div>
            <h2 className="font-heading font-black text-2xl">In-progress payroll runs</h2>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-ink hover:bg-ink hover:text-white" data-testid="drafts-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {drafts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground font-mono text-sm">
            No saved drafts yet. Use "Save as Draft" during any step of the wizard to keep an in-progress run.
          </div>
        ) : (
          <div className="divide-y-2 divide-ink">
            {drafts.map((d) => (
              <div key={d.id} className="p-4 flex items-center justify-between gap-4" data-testid={`draft-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-black">{d.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {d.period_start} → {d.period_end} · pay {d.pay_date} · step {d.step} · updated {new Date(d.updated_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onLoad(d)} data-testid={`draft-load-${d.id}`} className="hard-btn px-3 py-2 font-mono uppercase text-[10px] tracking-widest">Load →</button>
                  <button onClick={() => onDelete(d.id)} className="p-2 border-2 border-bad text-bad hover:bg-bad hover:text-white" data-testid={`draft-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
